/**
 * AgentPermissionRoute - Route wrapper that checks agent permissions
 * Blocks access to pages if agent doesn't have required permission
 */
import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAgentPermissions, type AgentPermission } from '@/hooks/useAgentPermissions';
import { useAuth } from '@/hooks/useAuth';
import { AgentLayout } from '@/components/portal/AgentLayout';
import { ShieldX, ArrowLeft, Home, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

interface AgentPermissionRouteProps {
  children: ReactNode;
  /** Single permission required */
  permission?: AgentPermission;
  /** Multiple permissions - any of them grants access */
  permissions?: AgentPermission[];
  /** If true, requires ALL permissions instead of ANY */
  requireAll?: boolean;
  /** Fallback URL if access denied */
  fallbackUrl?: string;
  /** Show the denied page in AgentLayout or redirect silently */
  showDeniedPage?: boolean;
}

export function AgentPermissionRoute({
  children,
  permission,
  permissions,
  requireAll = false,
  fallbackUrl = '/en/agent/schedule',
  showDeniedPage = true,
}: AgentPermissionRouteProps) {
  const { authInfo, isLoading: authLoading } = useAuth();
  const { hasPermission, hasAnyPermission, hasAllPermissions, isLoading: permLoading } = useAgentPermissions();

  // Show loader while checking
  if (authLoading || permLoading) {
    return (
      <AgentLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AgentLayout>
    );
  }

  // Supervisors/admins bypass permission checks
  if (authInfo?.internal_role && authInfo.internal_role !== 'agent') {
    return <>{children}</>;
  }

  // Check permissions
  let hasAccess = true;

  if (permission) {
    hasAccess = hasPermission(permission);
  }

  if (permissions && permissions.length > 0) {
    hasAccess = requireAll 
      ? hasAllPermissions(permissions)
      : hasAnyPermission(permissions);
  }

  if (!hasAccess) {
    if (!showDeniedPage) {
      return <Navigate to={fallbackUrl} replace />;
    }

    return (
      <AgentLayout>
        <AccessDeniedPage fallbackUrl={fallbackUrl} />
      </AgentLayout>
    );
  }

  return <>{children}</>;
}

function AccessDeniedPage({ fallbackUrl }: { fallbackUrl: string }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
      <div className="w-20 h-20 bg-destructive/10 rounded-full flex items-center justify-center mb-6">
        <ShieldX className="h-10 w-10 text-destructive" />
      </div>
      
      <h1 className="text-2xl font-bold text-foreground mb-2">
        Restricted Access
      </h1>
      
      <p className="text-muted-foreground max-w-md mb-8">
        You don't have permission to access this feature. 
        Contact your administrator if you need access.
      </p>

      <div className="flex gap-3">
        <Link to={fallbackUrl}>
          <Button variant="outline">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Go Back
          </Button>
        </Link>
        <Link to={fallbackUrl || `/${window.location.pathname.split('/')[1] || 'en'}/agent`}>
          <Button>
            <Home className="h-4 w-4 mr-2" />
            Dashboard
          </Button>
        </Link>
      </div>
    </div>
  );
}

// Mapping of routes to required permissions
// NOTE: /en/agent/mail is NOT listed here - it uses config-based auth (myEmailConfig?.is_active)
export const AGENT_ROUTE_PERMISSIONS: Record<string, AgentPermission | AgentPermission[]> = {
  // Channels - Voice
  '/en/agent/phone': 'use_dialpad',
  
  // Tickets / Cases
  '/en/agent/tickets': 'view_case',
  
  // Quality
  '/en/agent/qa': 'view_own_evaluations',
  '/en/agent/coaching': 'acknowledge_coaching',
  '/en/agent/learning': 'acknowledge_coaching',
  
  // Schedule & Time
  '/en/agent/schedule': 'view_schedule',
  '/en/agent/time': 'clock_in_out',
  
  // Knowledge Base
  '/en/agent/kb': 'view_kb_articles',
  
  // Announcements (always allowed - no specific permission)
  '/en/agent/announcements': 'view_schedule',
};

export default AgentPermissionRoute;
